import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function About() {
  const { t } = useTranslation('about');

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors mb-8 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          {t('backToHome')}
        </Link>

        <h1 className="text-4xl font-bold text-slate-900 mb-8">{t('title')}</h1>

        <div className="prose prose-slate max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('whoWeAre.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('whoWeAre.paragraph1')}
            </p>
            <p className="text-slate-700 leading-relaxed">
              {t('whoWeAre.paragraph2')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('ourMission.title')}</h2>
            <p className="text-slate-700 leading-relaxed">
              {t('ourMission.description')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('whatWeDo.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('whatWeDo.intro')}
            </p>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 mt-1"></i>
                <span><strong>{t('whatWeDo.services.marketAnalysis.title')}</strong> {t('whatWeDo.services.marketAnalysis.description')}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 mt-1"></i>
                <span><strong>{t('whatWeDo.services.leadEnablement.title')}</strong> {t('whatWeDo.services.leadEnablement.description')}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 mt-1"></i>
                <span><strong>{t('whatWeDo.services.businessIntelligence.title')}</strong> {t('whatWeDo.services.businessIntelligence.description')}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 mt-1"></i>
                <span><strong>{t('whatWeDo.services.strategicSupport.title')}</strong> {t('whatWeDo.services.strategicSupport.description')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('ourApproach.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('ourApproach.intro')}
            </p>
            <ol className="space-y-3 text-slate-700 list-decimal list-inside">
              <li><strong>{t('ourApproach.steps.listen.title')}</strong> {t('ourApproach.steps.listen.description')}</li>
              <li><strong>{t('ourApproach.steps.analyze.title')}</strong> {t('ourApproach.steps.analyze.description')}</li>
              <li><strong>{t('ourApproach.steps.strategize.title')}</strong> {t('ourApproach.steps.strategize.description')}</li>
              <li><strong>{t('ourApproach.steps.execute.title')}</strong> {t('ourApproach.steps.execute.description')}</li>
              <li><strong>{t('ourApproach.steps.optimize.title')}</strong> {t('ourApproach.steps.optimize.description')}</li>
            </ol>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('whyChoose.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              {t('whyChoose.intro')}
            </p>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <i className="ri-star-fill text-teal-600 mt-1"></i>
                <span><strong>{t('whyChoose.reasons.expertise.title')}</strong> {t('whyChoose.reasons.expertise.description')}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-star-fill text-teal-600 mt-1"></i>
                <span><strong>{t('whyChoose.reasons.customization.title')}</strong> {t('whyChoose.reasons.customization.description')}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-star-fill text-teal-600 mt-1"></i>
                <span><strong>{t('whyChoose.reasons.resultsDriven.title')}</strong> {t('whyChoose.reasons.resultsDriven.description')}</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-star-fill text-teal-600 mt-1"></i>
                <span><strong>{t('whyChoose.reasons.partnership.title')}</strong> {t('whyChoose.reasons.partnership.description')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('ourValues.title')}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('ourValues.values.integrity.title')}</h3>
                <p className="text-slate-700 text-sm">
                  {t('ourValues.values.integrity.description')}
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('ourValues.values.excellence.title')}</h3>
                <p className="text-slate-700 text-sm">
                  {t('ourValues.values.excellence.description')}
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('ourValues.values.innovation.title')}</h3>
                <p className="text-slate-700 text-sm">
                  {t('ourValues.values.innovation.description')}
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('ourValues.values.collaboration.title')}</h3>
                <p className="text-slate-700 text-sm">
                  {t('ourValues.values.collaboration.description')}
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">{t('getInTouch.title')}</h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              {t('getInTouch.intro')}
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('getInTouch.contactInfo')}</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <i className="ri-phone-line text-teal-600"></i>
                  <a href="tel:+32488139664" className="hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                    +32 488 13 96 64
                  </a>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <i className="ri-mail-line text-teal-600"></i>
                  <a href="mailto:david@novaventure.be" className="hover:text-teal-600 transition-colors cursor-pointer">
                    david@novaventure.be
                  </a>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <i className="ri-map-pin-line text-teal-600 mt-0.5"></i>
                  <span>Blaasveld Willebroek 2830, Belgium</span>
                </li>
              </ul>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            {t('backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
